import { Box, Stack, useToast } from "@chakra-ui/react";

import { AuthHeaderLayout } from "@/layouts/auth/AuthHeaderLayout";
import { AuthCard } from "@/components/card/AuthCard";
import { useLocation, useNavigate } from "react-router-dom";
import { useEffect, useState } from "react";
import { SubmitHandler, useForm } from "react-hook-form";
import { AuthButton } from "@/components/button/AuthButton";
import { useResetPasswordMutation } from "@/services/requests/auth";
import { PasswordInput } from "@/components/input/PasswordInput";
import { resetPasswordFormInputs } from "@/constant/formInputs";
import { passwordInputTypes } from "@/types/formInputTypes";

interface SearchDataProps {
	email: string | null;
	id: string | null;
	valid: string | null;
}

const ResetPassword = () => {
	const toast = useToast();
	const navigate = useNavigate();
	const location = useLocation();
	const [searchData, setSearchData] = useState<SearchDataProps>({
		email: "",
		id: "",
		valid: "",
	});

	const [resetPassword, { isSuccess, isLoading, isError, data }] =
		useResetPasswordMutation();

	const {
		register,
		handleSubmit,
		formState: { errors },
	} = useForm<passwordInputTypes>();

	// onsubmit function
	const onSubmit: SubmitHandler<passwordInputTypes> = async (data) => {
		localStorage.removeItem("guliva-email");
		if (data.newPassword === data.confirmNewPassword) {
			await resetPassword({
				newPassword: data.newPassword,
				email: searchData.email,
			});
		} else {
			toast({
				title: "New Password and Confirm Password do not match",
				status: "error",
				isClosable: true,
				position: "top-right",
			});
		}
	};

	// get query params
	useEffect(() => {
		const searchParams = new URLSearchParams(location.search);
		if (searchParams.has("email")) {
			const email = searchParams.get("email");
			const id = searchParams.get("id");
			const valid = searchParams.get("valid");
			setSearchData({ email, id, valid });
		}
	}, [location]);

	// check if password changed successfully
	useEffect(() => {
		if (isSuccess) {
			toast({
				title: data.message,
				status: "success",
				isClosable: true,
				position: "top-right",
			});
			navigate("/auth/reset-password-email");
		}
	}, [isSuccess]);

	// check if there is an error
	useEffect(() => {
		if (isError) {
			toast({
				title: "Oops! Something went wrong, please try again",
				status: "error",
				isClosable: true,
				position: "top-right",
			});
		}
	}, [isError]);

	return (
		<Box>
			<Stack spacing="0" bg="brand.white300">
				<AuthHeaderLayout />

				<AuthCard
					title="Reset Password"
					description="Good job, you clicked the link to reset your password. Enter the
					new password you would like to use."
					btnText="RESEND"
				>
					<Box as="form" onSubmit={handleSubmit(onSubmit)}>
						{resetPasswordFormInputs.map(
							({ label, type, placeholder, name, rules }) => (
								<PasswordInput
									key={name}
									type={type}
									label={label}
									placeholder={placeholder}
									name={name}
									id={name}
									register={register}
									rules={rules}
									errors={errors}
								/>
							)
						)}
						<AuthButton text="RESET PASSWORD" loading={isLoading} />
					</Box>
				</AuthCard>
			</Stack>
		</Box>
	);
};

export default ResetPassword;
